/* FILE: assets/sim_slideshow.js
   Simbiosis Slideshow Parallax (hero-style)

   Fixes:
   - Focal + zoom now reliably work even with looping clones:
     read base scale from the per-slide .slide__image-container (inline vars).
   - Keep Y applied to ALL slides every frame, preventing transition jitter.
*/

(() => {
  const ROOT_SELECTOR = "[data-sim-slideshow]";

  const clamp = (v, min, max) => (v < min ? min : v > max ? max : v);

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function isMobileViewport() {
    return window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
  }

  function parseBool(v) {
    return String(v) === "true";
  }

  function parseNum(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function easeSineOut(p) {
    return Math.sin((p * Math.PI) / 2);
  }

  function easeOutQuad(p) {
    return 1 - (1 - p) * (1 - p);
  }

  function applyCurve(p, curve) {
    if (curve === "linear") return p;
    if (curve === "ease_out") return easeOutQuad(p);
    return easeSineOut(p);
  }

  function readCssVarNumber(el, varName, fallback = 1) {
    if (!el) return fallback;
    const raw = getComputedStyle(el).getPropertyValue(varName);
    const n = parseFloat(String(raw).trim());
    return Number.isFinite(n) ? n : fallback;
  }

  class SimSlideshowParallax {
    constructor(root) {
      this.root = root;

      this.enabled = parseBool(root.dataset.parallaxEnabled);
      this.intensity = parseNum(root.dataset.parallaxIntensity, 0);
      this.curve = root.dataset.parallaxCurve || "ease_out";
      this.direction = root.dataset.parallaxDirection || "up";
      this.enableDesktop = parseBool(root.dataset.parallaxDesktop);
      this.enableMobile = parseBool(root.dataset.parallaxMobile);
      this.reduceMotionBehavior = root.dataset.reduceMotion || "force_disable";

      this.startY = 0;
      this.endY = 1;

      this.maxShift = 0;

      this.inView = false;
      this.pending = false;
      this.raf = 0;
      this.lastApplied = 0;

      this.io = null;
      this.mo = null;

      this._onIntersect = this._onIntersect.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._applyFrame = this._applyFrame.bind(this);
      this._onSlideChangeSignal = this._onSlideChangeSignal.bind(this);

      this._init();
    }

    _deviceAllowed() {
      const mobile = isMobileViewport();
      return mobile ? this.enableMobile : this.enableDesktop;
    }

    _effectiveEnabled() {
      if (!this.enabled) return false;
      if (prefersReducedMotion()) return this.reduceMotionBehavior === "subtle";
      return this._deviceAllowed();
    }

    _getAllSlides() {
      return Array.from(this.root.querySelectorAll("slideshow-slide"));
    }

    _getRepresentativeSlide() {
      return (
        this.root.querySelector("slideshow-slide:not([aria-hidden='true'])") ||
        this.root.querySelector("slideshow-slide[aria-hidden='false']") ||
        this.root.querySelector("slideshow-slide")
      );
    }

    _getMediaContainer(slide) {
      return slide ? slide.querySelector(".slide__image-container") : null;
    }

    _recomputeMaxShift() {
      const slide = this._getRepresentativeSlide();
      const h = slide?.clientHeight || 0;
      if (h <= 0) {
        this.maxShift = 0;
        return;
      }
      const maxByHeight = h * 0.18;
      this.maxShift = clamp(this.intensity, 0, maxByHeight);
    }

    _applyCombinedScaleToAllSlides() {
      const slides = this._getAllSlides();
      const enabled = this._effectiveEnabled();

      for (const slide of slides) {
        const container = this._getMediaContainer(slide);

        // Read base zoom from the container (inline vars survive cloning)
        const baseScale = readCssVarNumber(container, "--sim-slide-base-scale", 1);

        let parallaxScale = 1;
        if (enabled) {
          const h = slide?.clientHeight || 0;
          if (h > 0) {
            const maxByHeight = h * 0.18;
            const maxShift = clamp(this.intensity, 0, maxByHeight);
            const needed = 1 + (2 * maxShift) / h;
            parallaxScale = clamp(needed, 1, 1.28);
          }
        }

        const combined = clamp(baseScale * parallaxScale, 0.5, 2.0);
        slide.style.setProperty("--sim-slide-img-scale", String(combined));
      }
    }

    _recomputeScrollBounds() {
      const rect = this.root.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;

      const topDoc = rect.top + scrollY;
      const h = rect.height || this.root.clientHeight || 1;
      const vh = window.innerHeight || 1;

      this.startY = topDoc - vh;
      this.endY = topDoc + h;
      if (this.endY <= this.startY) this.endY = this.startY + 1;
    }

    _progress(scrollY) {
      const p = (scrollY - this.startY) / (this.endY - this.startY);
      return clamp(p, 0, 1);
    }

    _progressToOffsetCentered(progress01) {
      let centered = (0.5 - progress01) * 2; // +1..-1
      if (this.direction === "down") centered = -centered;
      return centered * this.maxShift;
    }

    _applyYToAllSlides(yPx) {
      const slides = this._getAllSlides();
      const val = `${yPx.toFixed(2)}px`;
      for (const slide of slides) slide.style.setProperty("--sim-slide-img-y", val);
    }

    _schedule() {
      if (this.pending) return;
      this.pending = true;
      this.raf = requestAnimationFrame(this._applyFrame);
    }

    _applyFrame() {
      this.pending = false;
      if (!this.inView) return;

      const scrollY = window.scrollY || window.pageYOffset || 0;
      let p = this._progress(scrollY);

      if (prefersReducedMotion() && this.reduceMotionBehavior === "subtle") {
        p = 0.5 + (p - 0.5) * 0.2;
      }

      const eased = applyCurve(p, this.curve);
      const target = this._progressToOffsetCentered(eased);

      const maxStep = Math.max(3, this.maxShift * 0.22);
      const delta = clamp(target - this.lastApplied, -maxStep, maxStep);
      const next = this.lastApplied + delta;

      this.lastApplied = next;

      // Apply to ALL slides so transition never resets then jumps
      this._applyYToAllSlides(next);
    }

    _onScroll() {
      this._schedule();
    }

    _onResize() {
      if (!this.inView) return;
      this._recomputeMaxShift();
      this._recomputeScrollBounds();
      this._applyCombinedScaleToAllSlides();
      this._schedule();
    }

    _onSlideChangeSignal() {
      if (!this.inView) return;
      this._recomputeMaxShift();
      this._applyCombinedScaleToAllSlides();
      this._schedule();
    }

    _watchSlideChanges() {
      try {
        this.mo = new MutationObserver(() => {
          this._onSlideChangeSignal();
        });

        this.mo.observe(this.root, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-hidden"]
        });
      } catch (e) {
        // no-op
      }
    }

    _onIntersect(entries) {
      const entry = entries && entries[0];
      if (!entry) return;

      const enabled = this._effectiveEnabled();

      if (!entry.isIntersecting || !enabled) {
        this.inView = false;
        window.removeEventListener("scroll", this._onScroll, { passive: true });

        this.lastApplied = 0;
        this._applyYToAllSlides(0);
        // Keep base zoom visible even when parallax disabled
        this._applyCombinedScaleToAllSlides();
        return;
      }

      this.inView = true;

      this._recomputeMaxShift();
      this._recomputeScrollBounds();
      this._applyCombinedScaleToAllSlides();

      window.addEventListener("scroll", this._onScroll, { passive: true });
      this._schedule();
    }

    _init() {
      // Ensure base zoom visible immediately
      this._applyCombinedScaleToAllSlides();
      this._applyYToAllSlides(0);

      this.root.addEventListener("slideshow:change", this._onSlideChangeSignal);
      this._watchSlideChanges();

      try {
        this.io = new IntersectionObserver(this._onIntersect, {
          root: null,
          rootMargin: "200px 0px 200px 0px",
          threshold: [0, 0.01]
        });
        this.io.observe(this.root);
      } catch (e) {
        this.inView = true;
        this._recomputeMaxShift();
        this._recomputeScrollBounds();
        window.addEventListener("scroll", this._onScroll, { passive: true });
        this._schedule();
      }

      window.addEventListener("resize", this._onResize, { passive: true });

      window.addEventListener(
        "load",
        () => {
          if (!this.inView) return;
          this._recomputeMaxShift();
          this._recomputeScrollBounds();
          this._applyCombinedScaleToAllSlides();
          this._schedule();
        },
        { once: true }
      );
    }

    destroy() {
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      if (this.mo) {
        this.mo.disconnect();
        this.mo = null;
      }

      this.root.removeEventListener("slideshow:change", this._onSlideChangeSignal);

      window.removeEventListener("resize", this._onResize);
      window.removeEventListener("scroll", this._onScroll, { passive: true });

      if (this.pending) {
        cancelAnimationFrame(this.raf);
        this.pending = false;
        this.raf = 0;
      }

      this._applyYToAllSlides(0);
    }
  }

  const instances = new Map();

  function mount(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new SimSlideshowParallax(root));
  }

  function unmount(root) {
    const inst = instances.get(root);
    if (!inst) return;
    inst.destroy();
    instances.delete(root);
  }

  function scan(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan());
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", (e) => {
    if (!e.target) return;
    scan(e.target);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    if (!e.target) return;
    e.target.querySelectorAll(ROOT_SELECTOR).forEach(unmount);
  });
})();
